// frontend/src/components/ProfilEntreprise.js
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import api from '../api';

function getBrandColor(nom) {
  const colors = ['#e63946','#2a9d8f','#e9c46a','#f4a261','#264653','#6d6875','#457b9d','#1d3557'];
  let hash = 0;
  for (let i = 0; i < (nom || '').length; i++) hash = nom.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(nom) {
  return nom ? nom.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : 'BTP';
}

const PLAN_LABELS = {
  trial:      { label: 'Essai gratuit', color: '#f4a261' },
  starter:    { label: 'Starter',       color: '#457b9d' },
  pro:        { label: 'Pro',           color: '#2a9d8f' },
  enterprise: { label: 'Enterprise',    color: '#1d3557' },
};

function ProfilEntreprise() {
  const [entreprise, setEntreprise] = useState(null);
  const [form, setForm]             = useState({});
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(false);

  const fetchEntreprise = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/users/entreprise');
      setEntreprise(res.data);
      setForm({
        nom:           res.data.nom || '',
        siret:         res.data.siret || '',
        email_contact: res.data.email_contact || '',
        telephone:     res.data.telephone || '',
        adresse:       res.data.adresse || '',
        code_postal:   res.data.code_postal || '',
        ville:         res.data.ville || '',
      });
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntreprise(); }, [fetchEntreprise]);

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.put('/users/entreprise', form);
      setSuccess(true);
      await fetchEntreprise();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="text-center mt-5">
      <Spinner animation="border" />
      <p className="mt-2">Chargement...</p>
    </div>
  );

  const brandColor = getBrandColor(entreprise?.nom);
  const initiales  = getInitials(entreprise?.nom);
  const plan       = PLAN_LABELS[entreprise?.plan] || PLAN_LABELS.trial;
  const trialEnd   = entreprise?.trial_ends_at ? new Date(entreprise.trial_ends_at) : null;
  const trialDays  = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000*60*60*24))) : null;

  return (
    <div style={{ maxWidth: '800px' }}>

      {/* Carte identité entreprise */}
      <div style={{
        background: `linear-gradient(135deg, ${brandColor} 0%, ${brandColor}cc 100%)`,
        borderRadius: '16px', padding: '28px', marginBottom: '28px',
        color: 'white', display: 'flex', alignItems: 'center', gap: '20px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: '-10px', top: '-10px',
          width: '160px', height: '160px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)' }} />

        <div style={{
          width: '72px', height: '72px', borderRadius: '18px',
          background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255,255,255,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '26px', fontWeight: '800', flexShrink: 0, letterSpacing: '-1px',
        }}>{initiales}</div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '22px', fontWeight: '800', marginBottom: '4px' }}>
            {entreprise?.nom}
          </div>
          {entreprise?.ville && (
            <div style={{ opacity: 0.85, fontSize: '14px' }}>
              📍 {entreprise.ville}
              {entreprise.siret && ` · SIRET ${entreprise.siret}`}
            </div>
          )}
          <div style={{ opacity: 0.75, fontSize: '13px', marginTop: '2px' }}>
            Membre depuis {entreprise?.created_at ? new Date(entreprise.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '—'}
          </div>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '12px', padding: '14px 20px', textAlign: 'center', flexShrink: 0,
        }}>
          <div style={{ fontSize: '13px', fontWeight: '700', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: '4px', opacity: 0.9 }}>
            {plan.label}
          </div>
          {trialDays !== null && (
            <>
              <div style={{ fontSize: '28px', fontWeight: '800', lineHeight: 1 }}>{trialDays}</div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>jours restants</div>
            </>
          )}
        </div>
      </div>

      {/* Formulaire */}
      <div style={{
        background: 'white', borderRadius: '16px', padding: '28px',
        boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
      }}>
        <h5 style={{ fontWeight: '700', color: '#1a1a2e', marginBottom: '24px' }}>
          Informations de l'entreprise
        </h5>

        {error   && <Alert variant="danger"  dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success">✅ Profil mis à jour avec succès !</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Nom */}
          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#555' }}>
              NOM DE L'ENTREPRISE *
            </Form.Label>
            <Form.Control
              type="text" name="nom" required value={form.nom}
              onChange={handleField} size="lg"
              style={{ borderRadius: '10px', border: '1px solid #e0e0e0', fontWeight: '600' }}
            />
          </Form.Group>

          {/* SIRET + Téléphone */}
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#555' }}>
                  SIRET
                </Form.Label>
                <Form.Control
                  type="text" name="siret" value={form.siret}
                  onChange={handleField} placeholder="123 456 789 00012"
                  style={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#555' }}>
                  TELEPHONE
                </Form.Label>
                <Form.Control
                  type="text" name="telephone" value={form.telephone}
                  onChange={handleField} placeholder="04 78 00 00 00"
                  style={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}
                />
              </Form.Group>
            </div>
          </div>

          {/* Email contact */}
          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#555' }}>
              EMAIL DE CONTACT
            </Form.Label>
            <Form.Control
              type="email" name="email_contact" value={form.email_contact}
              onChange={handleField} placeholder="contact@monentreprise.fr"
              style={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}
            />
          </Form.Group>

          {/* Adresse */}
          <Form.Group className="mb-4">
            <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#555' }}>
              ADRESSE
            </Form.Label>
            <Form.Control
              type="text" name="adresse" value={form.adresse}
              onChange={handleField} placeholder="123 rue du Bâtiment"
              style={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}
            />
          </Form.Group>

          {/* Code postal + Ville */}
          <div className="row">
            <div className="col-md-4">
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#555' }}>
                  CODE POSTAL
                </Form.Label>
                <Form.Control
                  type="text" name="code_postal" value={form.code_postal}
                  onChange={handleField} placeholder="69000"
                  style={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}
                />
              </Form.Group>
            </div>
            <div className="col-md-8">
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#555' }}>
                  VILLE
                </Form.Label>
                <Form.Control
                  type="text" name="ville" value={form.ville}
                  onChange={handleField} placeholder="Lyon"
                  style={{ borderRadius: '10px', border: '1px solid #e0e0e0' }}
                />
              </Form.Group>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <Button
              type="submit" disabled={saving}
              style={{
                background: brandColor, border: 'none',
                borderRadius: '10px', padding: '10px 28px',
                fontWeight: '600', fontSize: '14px',
              }}
            >
              {saving ? '⏳ Enregistrement...' : '💾 Enregistrer les modifications'}
            </Button>
          </div>
        </Form>
      </div>

      {/* Section danger */}
      <div style={{
        background: 'white', borderRadius: '16px', padding: '24px',
        boxShadow: '0 2px 20px rgba(0,0,0,0.06)', marginTop: '20px',
        border: '1px solid #fee2e2',
      }}>
        <h6 style={{ fontWeight: '700', color: '#e63946', marginBottom: '8px' }}>
          Zone sensible
        </h6>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '0' }}>
          Pour modifier votre plan ou supprimer votre compte, contactez le support.
        </p>
      </div>
    </div>
  );
}

export default ProfilEntreprise;
